import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/base/Button";
import { Input } from "@/components/base/Field";
import { useToast, ToastProvider } from "@/components/base/Toast";
import { resetPassword, STRONG_PASSWORD, STRONG_PASSWORD_HINT } from "@/services/authService";

/** Page ouverte depuis le lien « Mot de passe oublié » reçu par e-mail. */
function ResetPasswordForm() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [pwd, setPwd] = useState({ next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!STRONG_PASSWORD.test(pwd.next)) {
      notify(`Mot de passe trop faible : ${STRONG_PASSWORD_HINT}.`, "error");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      notify("La confirmation ne correspond pas au nouveau mot de passe.", "error");
      return;
    }
    setSaving(true);
    try {
      await resetPassword(token, pwd.next);
      navigate("/login", {
        replace: true,
        state: { notice: "Mot de passe modifié. Connectez-vous avec votre nouveau mot de passe." },
      });
    } catch (err) {
      notify(err instanceof Error ? err.message : "Modification impossible.", "error");
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-12">
      <div className="w-full max-w-md animate-slide-up rounded-lg border border-background-200 bg-background-50 p-6 sm:p-8">
        <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className="ri-lock-unlock-line text-xl"></i>
        </span>
        <h1 className="text-xl font-semibold font-heading text-foreground-950">Nouveau mot de passe</h1>

        {!token ? (
          <>
            <p className="mt-2 text-sm text-foreground-600">
              Ce lien est incomplet. Ouvrez le lien reçu par e-mail tel quel, ou refaites une demande depuis la
              page de connexion.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:underline"
            >
              <i className="ri-arrow-left-line"></i> Retour à la connexion
            </Link>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-foreground-500">
              Choisissez le mot de passe que vous utiliserez désormais pour vous connecter.
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Input
                label="Nouveau mot de passe"
                type="password"
                autoComplete="new-password"
                value={pwd.next}
                onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                hint={STRONG_PASSWORD_HINT}
                required
              />
              <Input
                label="Confirmer le mot de passe"
                type="password"
                autoComplete="new-password"
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                required
              />
              <Button type="submit" size="lg" className="w-full" icon="ri-shield-check-line" disabled={saving}>
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
              <Link
                to="/login"
                className="block text-center text-xs text-foreground-500 hover:text-foreground-800"
              >
                Retour à la connexion
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  return (
    <ToastProvider>
      <ResetPasswordForm />
    </ToastProvider>
  );
}
